<script>
  import { getContext, onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { ROUTES } from "@utils/routes.js";

  import WebsiteRules from "./UserInputModules/WebsiteRules.svelte";
  import ApplicationRules from "./UserInputModules/ApplicationRules.svelte";
  import AnonymizationRules from "./UserInputModules/AnonymizationRules.svelte";
  import ExceptionRules from "./UserInputModules/ExceptionRules.svelte";
  import SimpleBlacklist from "./UserInputModules/SimpleBlacklist.svelte";
  import SimpleAnonRules from "./UserInputModules/SimpleAnonRules.svelte";
  import Header from "../lib/components/Header.svelte";
  import ButtonDefault from "../lib/components/Button.svelte";
  import ContentSwitcher from "../lib/components/ContentSwitcher.svelte";
  import { InfoBlock, SectionWrapper } from "paxray-components-svelte";

  import Modal from "@components/Modal.svelte";
  import ButtonNew from "@components/ButtonNew.svelte";

  const urlParams = useUrlParams();
  const session = getContext("session");

  let idproject;
  let currentStep;
  let lastStoreStep;

  // Only sync the step while on this route, leaving it must not rewrite the next URL.
  $: isUserInputRoute = $urlParams?.pathname === ROUTES.userInput;

  $: if ($urlParams && isUserInputRoute) {
    idproject = $urlParams.idproject || $urlParams.project;

    const query = new URLSearchParams($urlParams.search);
    if (!query.has("currentStep")) {
      urlParams.updateParam(
        "currentStep",
        currentStep !== undefined ? currentStep : 0,
        { replace: true },
      );
    } else if ($urlParams.currentStep !== lastStoreStep) {
      lastStoreStep = $urlParams.currentStep;
      currentStep = $urlParams.currentStep;
    }
  }

  let stepsCompleted = [true, true, true, true];
  let firstOpen = [false, false, false, false];
  // Help videos replaced by the tour, they show the old rule dialogs.
  // let helpModalOpen = false;
  let showRestrictionsGuide = false;
  let restrictionsGuideStep = 0;
  let restrictionsGuideSteps = [];

  function startRestrictionsTour() {
    restrictionsGuideSteps = [
      {
        target: '[data-guide="restrictions-switcher"]',
        title: "Choose what to set up",
        text: "Switch between excluded apps, excluded websites and anonymization rules.",
      },
      {
        target: '[data-guide="restrictions-hint"]',
        title: "What this step does",
        text: "Read what the current step does before you add rules.",
      },
      {
        target: '[data-guide="restrictions-content"] > .custom-table',
        title: "Your rules",
        text: "Add a rule with the button on the right. Your own rules appear when you expand the row.",
      },
      {
        target: '[data-guide="restrictions-presets"]',
        title: "Preset lists",
        text: "These lists are excluded by default. Open them to see what is already covered.",
      },
      {
        target: '[data-guide="restrictions-submit"]',
        title: "Submit your rules",
        text: "Submit when you are done to move on to the next step.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    restrictionsGuideStep = 0;
    showRestrictionsGuide = restrictionsGuideSteps.length > 0;
  }

  onMount(() => registerViewTour(startRestrictionsTour));
  const stepOptions = [
    "Excluded Apps",
    "Excluded Websites",
    "Anonymization Rules",
    // Local-Opening Rules are hidden for now, participant entries are not used yet.
    // "Local-Opening Rules",
  ];
  const helpSteps = [
    {
      title: "Excluded Apps",
      description:
        "Limit the initial scope of the paxray sensor by defining exclusions for desktop apps.",
      // text: 'In this example we show you how to add the game "Solitaire" to the blacklist',
      // video: "/img/userinput/apps/explanation.mp4",
    },
    {
      title: "Excluded Websites",
      description:
        "Limit the initial scope of the paxray sensor by defining exclusions for websites and web apps.",
      // text: 'In this example we show you how to add the website "Amazon Music" to the blacklist',
      // video: "/img/userinput/websites/explanation.mp4",
    },
    {
      title: "Anonymization Rules",
      description:
        "Ensure the privacy of potentially sensitive information within apps or websites.",
      // text: "In this example we show you how to anonymize client names and IDs.",
      // video: "/img/userinput/anon/explanation.mp4",
    },
    {
      title: "Local-Opening Rules",
      description:
        "Ensure capturing of relevant applications by defining specific whitelist rules.",
      // text: "In this example we show you how to add Excel orders to the whitelist",
      // video: "/img/userinput/local/explanation.mp4",
    },
  ];

  $: currentHelp = helpSteps[Number(currentStep)] || null;
  $: breadcrumbsClickable = session?.isValid === true;

  $: if (
    isUserInputRoute &&
    currentStep !== undefined &&
    currentStep !== lastStoreStep
  ) {
    lastStoreStep = currentStep;
    urlParams.updateParam("currentStep", currentStep);
  }

  $$restProps;
</script>

<Header
  section="User Input"
  area="Setup"
  breadcrumb="Participant Restrictions"
  tooltipText="Review and adjust exclusions, anonymization rules, and local-opening rules for the paxray sensor."
  {breadcrumbsClickable}
>
  <ButtonDefault
    variant="secondary"
    size="sm"
    text="Help"
    on:click={startRestrictionsTour}
  />
  <!-- {#if currentHelp}
    <ButtonDefault
      variant="secondary"
      text="Help"
      on:click={() => (helpModalOpen = true)}
    />
  {/if} -->
</Header>

<!-- {#if currentHelp}
  <Modal
    bind:open={helpModalOpen}
    modalHeading={currentHelp.title}
    primaryButtonText="Okay"
    preventCloseOnClickOutside
    actions
    autowidth
    noSecondaryButton
    primaryButtonAction={() => (helpModalOpen = false)}
    on:click:button--primary={() => (helpModalOpen = false)}
    on:close={() => (helpModalOpen = false)}
  >
    <h5>{currentHelp.text}</h5>
    <br /><br /><br />
    <video style="width:100%" controls>
      <track kind="captions" /><track />
      <source src={currentHelp.video} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </Modal>
{/if} -->

<SectionWrapper fullscreen={true}>
  <div class="main" slot="main">
    {#if currentStep < 4}
      <div class="phase_switcher" data-guide="restrictions-switcher">
        <ContentSwitcher
          options={stepOptions}
          bind:selected={currentStep}
          fitContent
        />
      </div>
    {/if}
    {#if currentHelp}
      <div data-guide="restrictions-hint">
        <InfoBlock
          type="info"
          heading={currentHelp.title}
          message={currentHelp.description}
          showClose={false}
        />
      </div>
    {/if}
    <div class="wrapper" data-guide="restrictions-content">
      <!-- APPLICATION BLACKLIST -->
      {#if currentStep == 0}
        <ApplicationRules
          {idproject}
          bind:firstOpen={firstOpen[0]}
          bind:stepsCompleted={stepsCompleted[0]}
          bind:currentStep
        ></ApplicationRules>
      {:else if currentStep == 1}
        <WebsiteRules
          {idproject}
          bind:firstOpen={firstOpen[1]}
          bind:stepsCompleted={stepsCompleted[1]}
          bind:currentStep
        ></WebsiteRules>
      {:else if currentStep == 2}
        <AnonymizationRules
          {idproject}
          bind:firstOpen={firstOpen[2]}
          bind:stepsCompleted={stepsCompleted[2]}
          bind:currentStep
        ></AnonymizationRules>
      {:else if currentStep == 3}
        <ExceptionRules
          {idproject}
          bind:firstOpen={firstOpen[3]}
          bind:stepsCompleted={stepsCompleted[3]}
          bind:currentStep
        ></ExceptionRules>
      {:else if currentStep == 4}
        <div class="done_wrapper">
          <h1>All done!</h1>
          <h2>Thank you for your participation!</h2>
          <br /><br />
          <div class="button_wrapper">
            <ButtonNew
              text="Go Back"
              setting
              on:click={() => {
                // Was 3 (Local-Opening Rules), hidden for now.
                currentStep = 2;
              }}
            ></ButtonNew>
            <ButtonNew
              setting
              text="Go to Start"
              on:click={() => {
                currentStep = 0;
              }}
            ></ButtonNew>
          </div>
        </div>
      {:else if currentStep == 5}
        <SimpleBlacklist {idproject} bind:currentStep></SimpleBlacklist>
      {:else if currentStep == 6}
        <SimpleAnonRules {idproject} bind:currentStep></SimpleAnonRules>
      {/if}
    </div>
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showRestrictionsGuide}
  bind:currentStep={restrictionsGuideStep}
  steps={restrictionsGuideSteps}
  allowInteraction={false}
/>

<style>
  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .phase_switcher {
    display: flex;
    justify-content: flex-start;
  }

  .done_wrapper {
    width: 100%;
    text-align: center;
    margin-top: 300px;
  }

  .wrapper > :global(.information_fields) {
    display: none;
  }

  .button_wrapper {
    display: flex;
    justify-content: center;
    gap: 1rem;
  }
</style>
